import React, { useCallback } from 'react';
import { Box, TextField } from '@mui/material';
import { useMenuContext } from './MenuContext';
import { Icon } from '@ui/Icon';
import { makeStyles } from 'tss-react/mui';
import { MENU_ITEM_STYLE } from './menu.model';
import { useComponentUUID } from '@client/hooks/use-utils.hooks';
import { KeyboardKeys } from '@shared/models/keyboard.model';

export type MenuSearchProps = {
  placeholder?: string;
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: `${MENU_ITEM_STYLE.ICON_COLUMN_WIDTH}px 1fr`,
    alignItems: 'center',
    columnGap: theme.spacing(MENU_ITEM_STYLE.COLUMN_GAP),
    paddingLeft: theme.spacing(MENU_ITEM_STYLE.PADDING_X),
    paddingRight: theme.spacing(MENU_ITEM_STYLE.PADDING_X),
    paddingTop: theme.spacing(MENU_ITEM_STYLE.PADDING_Y),
    paddingBottom: theme.spacing(MENU_ITEM_STYLE.PADDING_Y),
    minHeight: MENU_ITEM_STYLE.MIN_HEIGHT,
  },
  iconSlot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'rgba(0, 0, 0, 0.3)',
  },
  field: {
    width: '100%',
    '& .MuiInputBase-root': {
      padding: 0,
      fontSize: 'inherit',
    },
    '& .MuiInputBase-input': {
      padding: 0,
    },
  },
}));

export const MenuSearch: React.FC<MenuSearchProps> = ({
  placeholder = 'Search...',
}) => {
  const { searchString, setSearchString } = useMenuContext();
  const { classes } = useStyles();
  // Same guard as SearchField: unnamed menu search inputs otherwise replay old queries.
  const generatedName = useComponentUUID();

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key !== KeyboardKeys.ESCAPE || !searchString) return;
      event.preventDefault();
      event.stopPropagation();
      setSearchString('');
    },
    [searchString, setSearchString],
  );
  return (
    <Box className={classes.root}>
      <span className={classes.iconSlot}>
        <Icon category="mui" icon="Search" />
      </span>
      <TextField
        className={classes.field}
        variant="standard"
        size="small"
        autoComplete="off"
        name={generatedName}
        value={searchString}
        onKeyDown={handleKeyDown}
        onChange={(event) => setSearchString(event.target.value)}
        placeholder={placeholder}
        fullWidth
        slotProps={{
          input: {
            disableUnderline: true,
          },
          htmlInput: {
            'data-menu-search': true,
          },
        }}
      />
    </Box>
  );
};

MenuSearch.displayName = 'MenuSearch';
